/*
 * Photo authentication flow (sections/main-authentication.liquid).
 *
 * Screens are the intro, one per photo step (data-screen="0", "1", ...) and the review screen. Each screen change
 * is pushed to history so the phone's back gesture moves back a step instead of leaving the page.
 *
 * Everything is saved to Fall Line Ops through the app proxy (proxy-url, i.e. /apps/authenticate): a draft is
 * started with the customer's email (or their account when logged in), each photo goes straight to storage through a
 * signed upload URL, and the draft's resume token is kept in this browser so the customer can carry on later. The
 * same token arrives as ?resume= from the "continue your authentication" email.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Common slips on a phone keyboard, offered as "Did you mean ...?"
const EMAIL_DOMAIN_FIXES = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gnail.com': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmail.co.k': 'hotmail.co.uk',
  'outlok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'yahoo.con': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'iclod.com': 'icloud.com',
  'icloud.con': 'icloud.com',
  'icoud.com': 'icloud.com',
  'btinternet.con': 'btinternet.com'
};

class AuthenticationFlow extends HTMLElement {
  connectedCallback() {
    this.screens = Array.from(this.querySelectorAll('[data-screen]'));
    this.steps = this.screens.filter((screen) => /^\d+$/.test(screen.dataset.screen));
    this.photos = new Map(); // step key -> { url, skipped, saving, error, file }
    this.storageKey = `lilacblue-authentication:${this.getAttribute('product-id')}`;
    this.submission = null; // { token, submissionId, email }

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('input', (event) => event.target.closest('[data-start-form]') && this.hideEmailSuggestion());
    window.addEventListener('popstate', (event) => this.show(event.state?.authenticationScreen ?? 'intro', false));
    window.addEventListener('beforeunload', (event) => {
      if (Array.from(this.photos.values()).some((photo) => photo.saving)) {
        event.preventDefault();
        event.returnValue = '';
      }
    });

    this.setUpDevice();
    this.resume();

    if (window.Shopify?.designMode) {
      this.listenToThemeEditor();
    }
  }

  disconnectedCallback() {
    this.editorListeners?.abort();
  }

  /*
   * Theme editor only: selecting a "Photo step" block in the sidebar shows that step in the preview, so example images
   * can be checked in place. Deselecting goes back to the intro. Customers never get these events.
   */
  listenToThemeEditor() {
    this.editorListeners = new AbortController();
    const { signal } = this.editorListeners;

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (this.contains(event.target) && event.target.matches('[data-step-key]')) {
          this.showScreen(event.target.dataset.screen);
        }
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (this.contains(event.target)) {
          this.showScreen('intro');
        }
      },
      { signal }
    );
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Server
   * ------------------------------------------------------------------------------------------------------------- */

  async request(action, body = {}) {
    const response = await fetch(`${this.getAttribute('proxy-url')}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, token: this.submission?.token ?? body.token })
    });

    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error || 'Something went wrong. Please check your connection and try again.');
    }
    return json;
  }

  /* Sent when a draft starts or continues: the product, and every step with its example image for the reviewers. */
  get product() {
    return {
      productId: this.getAttribute('product-id'),
      productHandle: this.getAttribute('product-handle'),
      productTitle: this.getAttribute('product-title'),
      stepCount: this.steps.length,
      steps: this.steps.map((step, index) => ({
        key: step.dataset.stepKey,
        title: step.dataset.stepTitle,
        index,
        skipLabel: step.dataset.skipLabel || null,
        exampleUrl: step.dataset.exampleUrl ? new URL(step.dataset.exampleUrl, window.location.href).href : null
      }))
    };
  }

  rememberToken(token) {
    try {
      token ? localStorage.setItem(this.storageKey, token) : localStorage.removeItem(this.storageKey);
    } catch {
      // Private browsing: the draft still continues from the emailed link
    }
  }

  storedToken() {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  /* Reopen a draft from ?resume=, this browser, or the customer's account. Nothing is created here. */
  async resume() {
    const url = new URL(window.location.href);
    const linkToken = url.searchParams.get('resume');

    if (linkToken) {
      url.searchParams.delete('resume');
      history.replaceState(history.state, '', url); // Keep the token out of shared or bookmarked URLs
    }

    const token = linkToken || this.storedToken();

    if (!token && !this.hasAttribute('logged-in')) {
      return;
    }

    try {
      const draft = await this.request('resume', { ...this.product, token });

      if (!draft) {
        this.rememberToken(null);
        return;
      }

      this.loadDraft(draft);

      const done = this.steps.filter((step) => this.photos.has(step.dataset.stepKey)).length;
      this.querySelector('[data-resume-count]').textContent = done;
      this.querySelector('[data-resume]').hidden = false;
      this.querySelector('[data-start-form]').hidden = true;
    } catch {
      // Not fatal: the customer can simply start a new one
    }
  }

  loadDraft(draft) {
    this.submission = { token: draft.token, submissionId: draft.submissionId, email: draft.email };
    this.rememberToken(draft.token);

    for (const photo of draft.photos) {
      const step = this.steps.find((screen) => screen.dataset.stepKey === photo.stepKey);

      if (step && (photo.url || photo.skipped)) {
        this.setPhoto(step, photo.skipped ? { skipped: true } : { url: photo.url });
      }
    }

    this.showEmailNote();
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Events
   * ------------------------------------------------------------------------------------------------------------- */

  async onSubmit(event) {
    event.preventDefault();

    if (event.target.matches('[data-start-form]')) {
      await this.start(event.target);
    } else if (event.target.matches('[data-email-form]')) {
      await this.changeEmail(event.target);
    }
  }

  async start(form) {
    const input = form.querySelector('input[type="email"]');
    const email = input?.value.trim();

    if (input) {
      if (!EMAIL_PATTERN.test(email)) {
        return this.showError(form, 'Please enter a valid email address.');
      }

      // Offer a correction once; pressing Start again goes ahead with what they typed
      const suggestion = this.emailSuggestion(email);

      if (suggestion && form.dataset.suggested !== email) {
        form.dataset.suggested = email;
        return this.showEmailSuggestion(suggestion);
      }
    }

    await this.whileBusy(form.querySelector('[type="submit"]'), async () => {
      try {
        this.loadDraft(await this.request('start', { ...this.product, email }));
        this.showError(form, null);
        this.enterAt(this.firstIncompleteStep() ?? 'review');
      } catch (error) {
        this.showError(form, error.message);
      }
    });
  }

  async changeEmail(form) {
    const email = form.querySelector('input[type="email"]').value.trim();

    if (!EMAIL_PATTERN.test(email)) {
      return;
    }

    await this.whileBusy(form.querySelector('[type="submit"]'), async () => {
      try {
        await this.request('email', { email });
        this.submission.email = email.toLowerCase();
        form.hidden = true;
        this.showEmailNote();
      } catch (error) {
        this.showError(form.closest('[data-screen]'), error.message);
      }
    });
  }

  onClick(event) {
    const button = event.target.closest('[data-action]');

    if (!button) {
      return;
    }

    const stepScreen = button.closest('[data-step-key]');

    switch (button.dataset.action) {
      case 'use-this-device':
        this.querySelectorAll('[data-device="phone"]').forEach((element) => (element.hidden = false));
        this.querySelectorAll('[data-device="desktop"]').forEach((element) => (element.hidden = true));
        break;

      case 'continue':
        this.enterAt(this.firstIncompleteStep() ?? 'review');
        break;

      case 'restart':
        this.restart();
        break;

      case 'use-suggestion': {
        const input = this.querySelector('[data-start-form] input[type="email"]');
        input.value = button.textContent;
        this.hideEmailSuggestion();
        break;
      }

      case 'change-email': {
        const form = this.querySelector('[data-email-form]');
        form.hidden = false;
        form.querySelector('input').focus();
        break;
      }

      case 'back':
        history.back();
        break;

      case 'skip':
        this.skip(stepScreen, button);
        break;

      case 'retake':
        stepScreen.querySelector('[data-photo-input]').click();
        break;

      case 'next': {
        const photo = this.photos.get(stepScreen.dataset.stepKey);

        if (photo?.error && photo.file) {
          this.upload(stepScreen, photo.file); // "Try again" after a failed upload
        } else if (photo && !photo.saving) {
          this.show(this.nextScreenAfter(stepScreen.dataset.screen));
        }
        break;
      }

      case 'goto':
        this.show(button.dataset.target);
        break;

      case 'checkout':
        this.checkout();
        break;
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-photo-input]');
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    input.value = ''; // Lets the customer pick the same file again
    this.upload(input.closest('[data-step-key]'), file);
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Photos
   * ------------------------------------------------------------------------------------------------------------- */

  async upload(stepScreen, file) {
    const stepKey = stepScreen.dataset.stepKey;
    this.setPhoto(stepScreen, { url: URL.createObjectURL(file), file, saving: true });

    try {
      const contentType = file.type || 'image/jpeg';
      const { uploadUrl, path } = await this.request('upload-url', { stepKey, contentType });
      const stored = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });

      if (!stored.ok) {
        throw new Error('upload failed');
      }

      await this.request('step', {
        stepKey,
        path,
        stepTitle: stepScreen.dataset.stepTitle,
        stepIndex: Number(stepScreen.dataset.screen)
      });

      this.updatePhoto(stepScreen, { saving: false, error: false, file: null });
    } catch {
      this.updatePhoto(stepScreen, { saving: false, error: true });
    }
  }

  async skip(stepScreen, button) {
    await this.whileBusy(button, async () => {
      try {
        await this.request('step', {
          stepKey: stepScreen.dataset.stepKey,
          skipped: true,
          skipLabel: stepScreen.dataset.skipLabel,
          stepTitle: stepScreen.dataset.stepTitle,
          stepIndex: Number(stepScreen.dataset.screen)
        });

        this.setPhoto(stepScreen, { skipped: true });
        this.show(this.nextScreenAfter(stepScreen.dataset.screen));
      } catch (error) {
        this.showError(stepScreen, error.message);
      }
    });
  }

  /* Saves (or clears, with null) the photo for a step and redraws that step's screen. */
  setPhoto(stepScreen, photo) {
    const key = stepScreen.dataset.stepKey;
    const previous = this.photos.get(key);

    if (previous?.url?.startsWith('blob:') && previous.url !== photo?.url) {
      URL.revokeObjectURL(previous.url);
    }

    photo ? this.photos.set(key, photo) : this.photos.delete(key);
    this.renderStep(stepScreen);
  }

  updatePhoto(stepScreen, changes) {
    const photo = this.photos.get(stepScreen.dataset.stepKey);

    if (photo) {
      Object.assign(photo, changes);
      this.renderStep(stepScreen);
    }
  }

  renderStep(stepScreen) {
    const photo = this.photos.get(stepScreen.dataset.stepKey);
    const hasPhoto = Boolean(photo?.url);
    const preview = stepScreen.querySelector('[data-preview]');
    const image = preview.querySelector('[data-preview-image]');
    const next = stepScreen.querySelector('[data-action="next"]');

    preview.hidden = !hasPhoto;
    hasPhoto ? (image.src = photo.url) : image.removeAttribute('src');
    stepScreen.querySelector('[data-compare]').classList.toggle('is-captured', hasPhoto);
    stepScreen.querySelector('[data-capture]').hidden = hasPhoto;
    stepScreen.querySelector('[data-confirm]').hidden = !hasPhoto;

    next.disabled = Boolean(photo?.saving);
    next.textContent = photo?.saving ? next.dataset.busyLabel : photo?.error ? 'Try again' : next.dataset.label;
    this.showError(stepScreen, photo?.error ? "Your photo didn't save. Check your connection and try again." : null);
  }

  isComplete(stepScreen) {
    const photo = this.photos.get(stepScreen.dataset.stepKey);
    return Boolean(photo && (photo.skipped || (!photo.saving && !photo.error)));
  }

  firstIncompleteStep() {
    return this.steps.find((step) => !this.isComplete(step))?.dataset.screen;
  }

  /* The next step, or the review screen once every step is done (so retakes from the review go straight back). */
  nextScreenAfter(index) {
    if (!this.firstIncompleteStep()) {
      return 'review';
    }

    const next = this.steps[Number(index) + 1];
    return next ? next.dataset.screen : this.firstIncompleteStep();
  }

  async restart() {
    const button = this.querySelector('[data-action="restart"]');

    await this.whileBusy(button, async () => {
      try {
        await this.request('discard');
      } catch {
        // Already gone; start fresh regardless
      }
    });

    this.submission = null;
    this.rememberToken(null);
    this.steps.forEach((step) => this.setPhoto(step, null));
    this.querySelector('[data-resume]').hidden = true;
    this.querySelector('[data-start-form]').hidden = false;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Checkout
   * ------------------------------------------------------------------------------------------------------------- */

  /*
   * A cart permalink straight to checkout: the email is filled in, and the submission id rides on the order twice
   * (hidden line item property, and cart attribute as a fallback) for the orders/paid webhook to find.
   */
  checkout() {
    if (!this.submission || this.firstIncompleteStep()) {
      return this.show(this.firstIncompleteStep() ?? 'intro');
    }

    const taken = this.steps.filter((step) => this.photos.get(step.dataset.stepKey)?.url).length;
    const skipped = this.steps.length - taken;
    const properties = {
      _submission_id: this.submission.submissionId,
      Photos: skipped > 0 ? `${taken} taken, ${skipped} not on the bag` : `${taken} taken`
    };

    const params = new URLSearchParams();
    params.set('properties', this.base64Url(JSON.stringify(properties)));
    params.set('attributes[_submission_id]', this.submission.submissionId);

    if (this.submission.email) {
      params.set('checkout[email]', this.submission.email);
    }

    window.location.href = `${this.getAttribute('cart-url')}/${this.getAttribute('variant-id')}:1?${params}`;
  }

  base64Url(text) {
    const bytes = new TextEncoder().encode(text);
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Screens and helpers
   * ------------------------------------------------------------------------------------------------------------- */

  /* On a computer (mouse or trackpad), show the QR code instead of the start button. */
  setUpDevice() {
    const isPhone = window.matchMedia('(pointer: coarse)').matches;

    if (isPhone || !this.hasAttribute('mobile-only')) {
      return;
    }

    this.querySelectorAll('[data-device="phone"]').forEach((element) => (element.hidden = true));
    this.querySelectorAll('[data-device="desktop"]').forEach((element) => (element.hidden = false));

    const holder = this.querySelector('[data-qr]');

    if (holder && window.QRCode) {
      const qrCode = document.createElement('qr-code');
      qrCode.setAttribute('identifier', this.getAttribute('handoff-url'));
      qrCode.setAttribute('width', '180');
      qrCode.setAttribute('height', '180');
      holder.append(qrCode);
    }
  }

  /*
   * Jump into the flow part-way through (continuing a draft). The steps before it are added to the browser history
   * first, so Back and the phone's back gesture go to the previous step rather than straight to the intro.
   */
  enterAt(name) {
    const index = name === 'review' ? this.steps.length : Number(name);

    for (const step of this.steps.slice(0, index)) {
      history.pushState({ authenticationScreen: step.dataset.screen }, '');
    }

    this.show(name);
  }

  show(name, pushHistory = true) {
    name = String(name);

    // Steps and the review need a started draft; the review also needs every step done
    if (name !== 'intro' && !this.submission) {
      name = 'intro';
    } else if (name === 'review' && this.firstIncompleteStep()) {
      name = this.firstIncompleteStep();
    }

    if (name === 'review') {
      this.renderReview();
    }

    this.showScreen(name);

    if (pushHistory) {
      history.pushState({ authenticationScreen: name }, '');
    }

    this.scrollIntoView({ block: 'start' });
  }

  showScreen(name) {
    this.screens.forEach((screen) => (screen.hidden = screen.dataset.screen !== name));
  }

  showEmailNote() {
    const note = this.querySelector('[data-email-note]');

    if (note && this.submission?.email) {
      note.querySelector('[data-email]').textContent = this.submission.email;
      note.hidden = false;
    }
  }

  emailSuggestion(email) {
    const [name, domain] = email.toLowerCase().split('@');
    const fixed = EMAIL_DOMAIN_FIXES[domain];
    return fixed ? `${name}@${fixed}` : null;
  }

  showEmailSuggestion(suggestion) {
    const holder = this.querySelector('[data-email-suggestion]');
    holder.querySelector('button').textContent = suggestion;
    holder.hidden = false;
  }

  hideEmailSuggestion() {
    const holder = this.querySelector('[data-email-suggestion]');

    if (holder) {
      holder.hidden = true;
    }
  }

  showError(scope, message) {
    const banner = scope.querySelector('[data-error]');

    if (banner) {
      banner.textContent = message ?? '';
      banner.hidden = !message;
    }
  }

  async whileBusy(button, work) {
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      await work();
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  renderReview() {
    const grid = this.querySelector('[data-review-grid]');

    grid.replaceChildren(
      ...this.steps.map((step) => {
        const photo = this.photos.get(step.dataset.stepKey);
        const item = document.createElement('li');
        const button = document.createElement('button');
        const caption = document.createElement('span');

        button.type = 'button';
        button.className = 'authentication-flow__review-item';
        button.dataset.action = 'goto';
        button.dataset.target = step.dataset.screen;

        if (photo?.url) {
          const image = document.createElement('img');
          image.src = photo.url;
          image.alt = '';
          button.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'authentication-flow__review-skipped text-xxs text-subdued';
          placeholder.textContent = step.dataset.skipLabel || 'Skipped';
          button.append(placeholder);
        }

        caption.className = 'text-xxs';
        caption.textContent = step.dataset.stepTitle;
        button.append(caption);
        item.append(button);

        return item;
      })
    );
  }
}

if (!window.customElements.get('authentication-flow')) {
  window.customElements.define('authentication-flow', AuthenticationFlow);
}
